import { useMutation } from "@tanstack/react-query";
import {
	createConfigurationId,
	addConfigurationToCart,
	updateSmallCart,
} from "../services/cartService";
import type { ComponentSelections } from "../utils/componentSelections";
import { triggerCustomEvent } from "../utils/eventHelpers.ts";

const MESSAGE_LEVEL_ERROR = "error";
const UNABLE_TO_ADD_INVALID_PRODUCT_TYPE_KEY = "cart.product.unabletoaddinvalidproducttype";
const CART_ERROR_MISMATCH_CONFIGBUNDLE_KEY = "cart.error.mismatch.configbundle";
const ALWAYS_INCLUDED_SELECTION_TYPE = "AlwaysIncluded";

interface ProductGroupEntry {
	id: string;
	product?: { id: string } | null;
	selectionType: string;
}

interface UseAddToCartOptions {
	configBundleId: string;
	selectedComponents: ComponentSelections;
	productGroups: ProductGroupEntry[];
	onSuccess?: (params: { renewalPeriod?: string }) => void;
	onError?: (error: string) => void;
}

export const useAddToCart = ({
	configBundleId,
	selectedComponents,
	productGroups,
	onSuccess,
	onError,
}: UseAddToCartOptions) => {
	const addToCartMutation = useMutation({
		mutationFn: async (params?: { renewalPeriod?: string }) => {
			const renewalPeriod = params?.renewalPeriod;

			const products = productGroups
				.map((group) => {
					if (group.selectionType === ALWAYS_INCLUDED_SELECTION_TYPE) {
						return group.product?.id
							? { matnr: group.product.id, productGroupId: group.id }
							: null;
					}

					const selection = selectedComponents[group.id];
					if (selection?.productId === null) {
						return null;
					}
					const productId = selection?.productId ?? group.product?.id;

					if (!productId) {
						return null;
					}

					return {
						matnr: productId,
						productGroupId: group.id,
					};
				})
				.filter(Boolean) as Array<{ matnr: string; productGroupId: string }>;

			const configurationId = await createConfigurationId(
				configBundleId,
				products
			);

			const cartResponse = await addConfigurationToCart(
				configBundleId,
				configurationId,
				renewalPeriod
			);

			const invalidProductTypeMessage = cartResponse.messages.find(
				(message) => message.key === UNABLE_TO_ADD_INVALID_PRODUCT_TYPE_KEY
			);

			if (invalidProductTypeMessage) {
				throw new Error(invalidProductTypeMessage.message);
			}

			const productMismatch = cartResponse.messages.find(
				(message) => message.key === CART_ERROR_MISMATCH_CONFIGBUNDLE_KEY,
			);

			if (productMismatch) {
				throw new Error(productMismatch.message);
			}

			const errorMessages = cartResponse.messages.filter(
				(message) => message.level === MESSAGE_LEVEL_ERROR
			);

			if (errorMessages.length > 0) {
				throw new Error(
					errorMessages.map((msg) => msg.message).join(" ")
				);
			}

			return { cartResponse, renewalPeriod, configurationId };
		},
		onSuccess: ({ cartResponse, renewalPeriod, configurationId}) => {
			updateSmallCart(cartResponse);

			triggerCustomEvent("openAddToCartPopup", {
				detail: {
					productId: configBundleId,
					renewalPeriod: renewalPeriod,
					configBundleConfigurationId: configurationId,
				},
			});

			onSuccess?.({ renewalPeriod });
		},
		onError: (error: Error) => {
			onError?.(error.message || "Failed to add to cart");
		},
	});

	return {
		addToCart: (params?: { renewalPeriod?: string }) => addToCartMutation.mutate(params),
		isAdding: addToCartMutation.isPending,
		error: addToCartMutation.error,
	};
};